import { LoadingIndicatorComponent } from '../../components/loading-indicator/loading-indicator.component';
import { Component, OnInit, OnDestroy, ElementRef, ViewChild, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SankhyaGatewayService } from '../../services/sankhya-gateway.service';
import { EmpresaStateService } from '../../services/empresa-state.service';
import { PermissaoAcessoService } from '../../services/permissao-acesso.service';
import { AerosysGatewayService, AerosysEmpresa, AerosysVendaConferencia } from '../../services/aerosys-gateway.service';
import { Subscription, interval } from 'rxjs';

export interface ConferenciaLinha {
  nunota: number;
  dtneg?: string;
  prioridade: string;
  imediato: string;
  nomeCliente: string;
  vendedor: string;
  status: string;
  prefixo?: string;
  confNome?: string;    // Conferente (será populado quando GPL_CONFERENCIA_ESTOQUE existir)
  dataInicio?: string; // Início da conferência
  qtdConferidos: number;
  totalItens: number;

  // ── Integração Aerosys (Sistema Legado) ──────────────────────────────────
  // Mesmo padrão do Painel de Separação (ver paineis-separacao.component.ts) --
  // linhas oriundas do Aerosys são convertidas para este mesmo formato (ver
  // _mapLegadoParaLinha) para que a MESMA tabela sirva pras duas fontes.
  // Remover junto com o restante do bloco "Vendas do Sistema Legado" abaixo
  // quando o Aerosys for desativado.
  origem?: 'sankhya' | 'aerosys';
  numTituloLegado?: string;
  /** "Sininho" -- itens com pendência "Item no outro Galpão" já RESOLVIDA
   *  (alguém já buscou) mas ainda não confirmados na contagem -- só existe
   *  pro fluxo Legado (ver ConferenciaPendenciaLegado no Aerosys). */
  itensPendenciaResolvida?: number;
}

@Component({
  selector: 'app-paineis-conferencia',
  standalone: true,
  imports: [LoadingIndicatorComponent, CommonModule, FormsModule],
  templateUrl: './paineis-conferencia.component.html',
  styleUrl: './paineis-conferencia.component.css'
})
export class PaineisConferenciaComponent implements OnInit, OnDestroy {
  @ViewChild('painelWrapper') painelWrapper!: ElementRef;

  // ── Atualização Automática ─────────────────────────────────
  intervaloAtualizacao = 30;
  tempoRestante = 30;
  private tickSub?: Subscription;

  // ── Paginação ──────────────────────────────────────────────
  paginaAtual = 1;
  itensPorPagina = 17;
  readonly opcoesPorPagina = [17, 20, 25, 50, 100];

  // ── Estado ─────────────────────────────────────────────────
  carregando = false;
  pedidos: ConferenciaLinha[] = [];
  erro = '';
  telaCheiaAtiva = false;

  private sub?: Subscription;
  private _empresaSub?: Subscription;

  // ── Vendas do Sistema Legado (Aerosys) — fonte alternativa da tabela ─────
  // Mesmo desenho do Painel de Separação (ver paineis-separacao.component.ts):
  // troca automática de fonte de dados baseada na permissão PAIAER do usuário
  // (resource GPL_PainelConferencia) — quem TEM a permissão vê SOMENTE as
  // vendas do Aerosys em conferência nesta tabela; quem NÃO tem vê SOMENTE os
  // pedidos do Sankhya, como sempre foi.
  podeVerVendasLegado = false;
  legadoAberto = false;
  legadoEmpresas: AerosysEmpresa[] = [];
  legadoEmpresasCarregando = false;
  legadoEmpresasErro = '';
  legadoEmpresaSelecionada: AerosysEmpresa | null = null;
  legadoLista: AerosysVendaConferencia[] = [];
  legadoCarregando = false;
  legadoErro = '';

  private readonly LEGADO_EMPRESAS_PERMITIDAS = [1, 3, 5, 7, 8, 9, 13, 15, 17];
  private readonly LEGADO_PREF_STORAGE_KEY = 'gpl_painel_conferencia_legado_pref';

  /** Fonte de dados da tabela: usuário com permissão PAIAER → SOMENTE vendas
   *  do Aerosys em conferência; sem a permissão → SOMENTE pedidos do Sankhya. */
  get linhas(): ConferenciaLinha[] {
    if (this.legadoAberto) {
      return this.legadoLista.map(v => this._mapLegadoParaLinha(v));
    }
    return this.pedidos;
  }

  /** Loading da fonte ativa no momento (Sankhya ou Aerosys). */
  get carregandoLinhas(): boolean {
    return this.legadoAberto ? this.legadoCarregando : this.carregando;
  }

  /** Enquanto o usuário ainda está escolhendo a empresa do Aerosys, não faz
   *  sentido mostrar "nenhum pedido encontrado" embaixo. */
  get aguardandoEscolhaEmpresaLegado(): boolean {
    return this.legadoAberto && !this.legadoEmpresaSelecionada;
  }

  constructor(
    private gateway: SankhyaGatewayService,
    private aerosysGateway: AerosysGatewayService,
    private permissaoAcesso: PermissaoAcessoService,
    readonly empresaState: EmpresaStateService
  ) {}

  ngOnInit(): void {
    this.buscar();
    this.iniciarTimer();

    // Isolamento por empresa: painel gerencial (codusu=0, todos os conferentes)
    // filtrado pela empresa logada — recarrega ao trocar a empresa selecionada.
    this._empresaSub = this.empresaState.empresaMudou.subscribe(() => {
      this.buscar();
    });

    this.permissaoAcesso.podeVerVendasLegadoConferenciaPainel().then(pode => {
      this.podeVerVendasLegado = pode;
      this.legadoAberto = pode;
      if (pode) {
        this.carregarLegadoEmpresas();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.sub) this.sub.unsubscribe();
    this._empresaSub?.unsubscribe();
    this.pararTimer();
  }

  /** Nome de exibição da empresa atualmente selecionada (badge no cabeçalho,
   *  visível mesmo em tela cheia). */
  get nomeEmpresaAtual(): string {
    const emp = this.empresaState.opcoes.find(e => e.codEmp === this.empresaState.selecionada);
    return emp?.nomeFant || emp?.razaoAbrev || '';
  }

  // ── Timer ──────────────────────────────────────────────────

  iniciarTimer(): void {
    this.pararTimer();
    if (this.intervaloAtualizacao > 0) {
      this.tempoRestante = this.intervaloAtualizacao;
      this.tickSub = interval(1000).subscribe(() => {
        this.tempoRestante--;
        if (this.tempoRestante <= 0) {
          this.atualizar();
          this.tempoRestante = this.intervaloAtualizacao;
        }
      });
    }
  }

  pararTimer(): void {
    if (this.tickSub) this.tickSub.unsubscribe();
  }

  onIntervaloChange(): void {
    if (this.intervaloAtualizacao >= 1) {
      this.iniciarTimer();
    } else {
      this.pararTimer();
      this.tempoRestante = 0;
    }
  }

  // ── Tela Cheia ─────────────────────────────────────────────

  alternarTelaCheia(): void {
    const el = this.painelWrapper.nativeElement;
    if (!document.fullscreenElement) {
      el.requestFullscreen().then(() => { this.telaCheiaAtiva = true; }).catch(() => {});
    } else {
      document.exitFullscreen().then(() => { this.telaCheiaAtiva = false; });
    }
  }

  @HostListener('document:fullscreenchange')
  onFullscreenChange() {
    this.telaCheiaAtiva = !!document.fullscreenElement;
  }

  // ── Paginação ──────────────────────────────────────────────

  get totalPaginas(): number {
    return Math.max(1, Math.ceil(this.linhas.length / this.itensPorPagina));
  }

  get pedidosPagina(): ConferenciaLinha[] {
    const inicio = (this.paginaAtual - 1) * this.itensPorPagina;
    return this.linhas.slice(inicio, inicio + this.itensPorPagina);
  }

  get paginas(): number[] {
    const total = this.totalPaginas;
    const atual = this.paginaAtual;
    const delta = 2;
    const range: number[] = [];
    for (let i = Math.max(1, atual - delta); i <= Math.min(total, atual + delta); i++) {
      range.push(i);
    }
    return range;
  }

  irParaPagina(p: number): void {
    if (p < 1 || p > this.totalPaginas) return;
    this.paginaAtual = p;
  }

  onItensPorPaginaChange(): void {
    this.paginaAtual = 1;
  }

  // ── Busca ──────────────────────────────────────────────────

  /**
   * Ponto único de "atualizar a lista" -- usado pelo timer automático e pelo
   * botão manual "Atualizar". Refaz a busca da fonte REALMENTE exibida no
   * momento (ver getter `linhas`): Aerosys/Legado quando `legadoAberto`,
   * Sankhya (`buscar()`) caso contrário (mesmo bug corrigido no Painel de
   * Separação -- ver comentário em paineis-separacao.component.ts#atualizar).
   */
  atualizar(): void {
    this.tempoRestante = this.intervaloAtualizacao;
    if (this.legadoAberto) {
      if (this.legadoEmpresaSelecionada) {
        this.buscarVendasLegado();
      }
    } else {
      this.buscar();
    }
  }

  buscar(): void {
    this.carregando = true;
    this.erro = '';
    this.tempoRestante = this.intervaloAtualizacao;

    // Antes chamava gateway.buscarPedidosConferencia() (GET /separacao/buscar-conferencia),
    // um endpoint legado sem NENHUM filtro de empresa — misturava pedidos de todas as
    // empresas no painel. Trocado pelo mesmo endpoint filtrado que a tela operacional de
    // Conferência já usa (GET /conferencia/buscar), com codusu=0 pra manter a visão
    // gerencial (todos os conferentes, não só o usuário logado).
    const codEmp = Number(this.empresaState.selecionada ?? 1);
    this.sub = this.gateway.buscarPedidosConferenciaEstoque(0, codEmp).subscribe({
      next: (res: any) => {
        this.carregando = false;
        if (res?.sucesso && Array.isArray(res.pedidos)) {
          this.pedidos = res.pedidos;
        } else {
          this.erro = res?.erro || 'Erro desconhecido ao carregar pedidos de conferência.';
        }
        this.paginaAtual = 1;
      },
      error: () => {
        this.carregando = false;
        this.erro = 'Falha de comunicação com o servidor. Tente novamente.';
      }
    });
  }

  // ── Helpers ────────────────────────────────────────────────

  statusClass(status: string): string {
    const s = (status ?? '').toLowerCase();
    if (s === 'separacao total') return 'status-sep-total';
    if (s === 'conferencia' || s === 'conferência') return 'status-conferencia';
    return 'status-outro';
  }

  get totalConferencia(): number {
    return this.linhas.filter(p => {
      const s = (p.status ?? '').toLowerCase();
      return s === 'conferencia' || s === 'conferência';
    }).length;
  }

  get totalAguardando(): number {
    return this.linhas.filter(p =>
      (p.status ?? '').toLowerCase() === 'separacao total'
    ).length;
  }

  // ── Vendas do Sistema Legado (Aerosys) — fonte alternativa da tabela ─────
  // Bloco isolado de propósito (mesmo padrão do Painel de Separação) — todo
  // ele pode ser removido junto com a permissão PAIAER quando o Aerosys for
  // desativado, sem afetar o restante do Painel de Conferência.

  /** Converte uma venda "rica" do Aerosys (VendaConferenciaLegadoDTO) para o
   *  mesmo formato de linha usado pelos pedidos do Sankhya. */
  private _mapLegadoParaLinha(v: AerosysVendaConferencia): ConferenciaLinha {
    const totalItens = v.totalItens ?? 0;
    const qtdConferidos = v.itensConferidos ?? 0;
    const imediatoTruthy = ['S', 'SIM', 'TRUE', '1'].includes((v.fatImediato ?? '').toUpperCase());
    return {
      nunota: -Math.abs(v.id),
      dtneg: this._formatarDataBr(v.dataEmissao, false),
      prioridade: v.prioridade || 'Normal',
      imediato: imediatoTruthy ? 'Sim' : 'Não',
      nomeCliente: v.clienteNome || '—',
      vendedor: v.vendedorNome || '—',
      status: v.status || '—',
      prefixo: v.prefixoAeronave || undefined,
      confNome: v.conferenteNome || undefined,
      qtdConferidos,
      totalItens,
      origem: 'aerosys',
      numTituloLegado: v.numTitulo || String(v.id),
      itensPendenciaResolvida: v.itensPendenciaResolvida ?? 0,
    };
  }

  /** Normaliza uma data (ISO ou epoch) para o padrão DD/MM/YYYY usado pelas colunas do Painel. */
  private _formatarDataBr(valor: string | null | undefined, comHora: boolean): string | undefined {
    if (!valor) return undefined;
    const d = new Date(valor);
    if (isNaN(d.getTime())) return undefined;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    if (!comHora) return `${dd}/${mm}/${yyyy}`;
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  }

  carregarLegadoEmpresas(): void {
    this.legadoEmpresasCarregando = true;
    this.legadoEmpresasErro = '';
    this.aerosysGateway.listarEmpresas().subscribe({
      next: (lista) => {
        const todas = Array.isArray(lista) ? lista : [];
        this.legadoEmpresas = todas.filter(e => this.LEGADO_EMPRESAS_PERMITIDAS.includes(Number(e.id)));
        this.legadoEmpresasCarregando = false;

        const empresaLembrada = this._restaurarEmpresaLegado();
        let alvo = empresaLembrada != null
          ? this.legadoEmpresas.find(e => Number(e.id) === empresaLembrada)
          : undefined;
        if (!alvo) {
          alvo = this.legadoEmpresas.find(e => Number(e.id) === 1);
        }
        if (alvo) this.selecionarLegadoEmpresa(alvo, false);
      },
      error: (err: any) => {
        this.legadoEmpresasErro = err?.error?.detail ?? err?.message ?? 'Erro ao carregar empresas do Aerosys.';
        this.legadoEmpresasCarregando = false;
      }
    });
  }

  selecionarLegadoEmpresa(empresa: AerosysEmpresa, persistir = true): void {
    this.legadoEmpresaSelecionada = empresa;
    if (persistir) this._salvarEmpresaLegado(empresa.id);
    this.buscarVendasLegado();
  }

  /** Handler do <select> de empresa no cabeçalho -- só troca se for uma empresa diferente da atual. */
  onLegadoEmpresaSelectChange(id: number | string | null): void {
    if (id == null) return;
    const empresa = this.legadoEmpresas.find(e => Number(e.id) === Number(id));
    if (empresa && Number(empresa.id) !== Number(this.legadoEmpresaSelecionada?.id)) {
      this.selecionarLegadoEmpresa(empresa);
    }
  }

  buscarVendasLegado(): void {
    if (!this.legadoEmpresaSelecionada) return;
    this.legadoCarregando = true;
    this.legadoErro = '';

    this.aerosysGateway.listarConferenciaPainelLegado(this.legadoEmpresaSelecionada.id).subscribe({
      next: (lista) => {
        this.legadoLista = Array.isArray(lista) ? lista : [];
        this.legadoCarregando = false;
      },
      error: (err: any) => {
        this.legadoErro = err?.error?.detail ?? err?.message ?? 'Erro ao buscar Vendas do Sistema Legado.';
        this.legadoCarregando = false;
      }
    });
  }

  // ── Preferência persistida (localStorage) ────────────────────────
  // A fonte (Sankhya x Aerosys) já é automática pela permissão PAIAER — só
  // resta lembrar QUAL empresa do Aerosys o usuário escolheu.

  private _salvarEmpresaLegado(empresaId: number | null): void {
    try {
      localStorage.setItem(this.LEGADO_PREF_STORAGE_KEY, JSON.stringify({ empresaId }));
    } catch {
      // localStorage indisponivel (modo privado etc.) - sem persistencia, sem quebrar a tela.
    }
  }

  private _restaurarEmpresaLegado(): number | null {
    try {
      const raw = localStorage.getItem(this.LEGADO_PREF_STORAGE_KEY);
      if (!raw) return null;
      const pref = JSON.parse(raw);
      return typeof pref?.empresaId === 'number' ? pref.empresaId : null;
    } catch {
      return null;
    }
  }
}
